import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, ArrowRight, Users, Loader2, Brain, Calendar } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useLanguage } from "@/i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;


const COUNTRIES = [
  { value: "Austria", en: "Austria", de: "Österreich" },
  { value: "Germany", en: "Germany", de: "Deutschland" },
  { value: "Switzerland", en: "Switzerland", de: "Schweiz" }
];

const INDUSTRIES = [
  { value: "Automotive", en: "Automotive", de: "Automotive" },
  { value: "B2B Services", en: "B2B Services", de: "B2B-Dienstleistungen" },
  { value: "E-commerce & Retail", en: "E-commerce & Retail", de: "E-Commerce & Einzelhandel" },
  { value: "Education & E-learning", en: "Education & E-learning", de: "Bildung & E-Learning" },
  { value: "Entertainment & Media", en: "Entertainment & Media", de: "Unterhaltung & Medien" },
  { value: "Fashion & Apparel", en: "Fashion & Apparel", de: "Mode & Bekleidung" },
  { value: "Finance & Banking", en: "Finance & Banking", de: "Finanzen & Banking" },
  { value: "Fitness & Sports", en: "Fitness & Sports", de: "Fitness & Sport" },
  { value: "Food & Beverage", en: "Food & Beverage", de: "Lebensmittel & Getränke" },
  { value: "Gaming", en: "Gaming", de: "Gaming" },
  { value: "Healthcare & Wellness", en: "Healthcare & Wellness", de: "Gesundheit & Wellness" },
  { value: "Real Estate", en: "Real Estate", de: "Immobilien" },
  { value: "Technology & Software", en: "Technology & Software", de: "Technologie & Software" },
  { value: "Travel & Hospitality", en: "Travel & Hospitality", de: "Reisen & Gastgewerbe" }
];

// Generation definitions with birth years and current age ranges (as of 2025)
const GENERATIONS = [
  { 
    id: "gen-alpha", 
    name: { en: "Gen Alpha", de: "Generation Alpha" },
    birthYears: "2013-2025", 
    ageRange: { en: "0-12 years old", de: "0-12 Jahre alt" },
    ageMin: 0, 
    ageMax: 12,
    color: "from-pink-500 to-purple-500"
  },
  { 
    id: "gen-z", 
    name: { en: "Gen Z", de: "Generation Z" },
    birthYears: "1997-2012", 
    ageRange: { en: "13-28 years old", de: "13-28 Jahre alt" },
    ageMin: 13, 
    ageMax: 28,
    color: "from-cyan-500 to-blue-500"
  },
  { 
    id: "millennials", 
    name: { en: "Millennials", de: "Millennials" },
    birthYears: "1981-1996", 
    ageRange: { en: "29-44 years old", de: "29-44 Jahre alt" },
    ageMin: 29, 
    ageMax: 44,
    color: "from-green-500 to-emerald-500"
  },
  { 
    id: "gen-x", 
    name: { en: "Gen X", de: "Generation X" },
    birthYears: "1965-1980", 
    ageRange: { en: "45-60 years old", de: "45-60 Jahre alt" },
    ageMin: 45, 
    ageMax: 60,
    color: "from-orange-500 to-amber-500"
  },
  { 
    id: "boomers", 
    name: { en: "Baby Boomers", de: "Babyboomer" },
    birthYears: "1946-1964", 
    ageRange: { en: "61-79 years old", de: "61-79 Jahre alt" },
    ageMin: 61, 
    ageMax: 79,
    color: "from-red-500 to-rose-500"
  },
  { 
    id: "silent", 
    name: { en: "Silent Gen", de: "Stille Generation" },
    birthYears: "1928-1945", 
    ageRange: { en: "80+ years old", de: "80+ Jahre alt" },
    ageMin: 80, 
    ageMax: 97,
    color: "from-slate-500 to-zinc-500"
  }
];

export default function SetupPage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    selectedGenerations: ["gen-z", "millennials"],
    genders: ["male", "female"],
    country: "",
    industry: "",
    agentCount: 5
  });

  const handleGenerationToggle = (genId) => {
    setFormData(prev => {
      const isSelected = prev.selectedGenerations.includes(genId);
      if (isSelected) {
        return {
          ...prev,
          selectedGenerations: prev.selectedGenerations.filter(g => g !== genId)
        };
      } else {
        return {
          ...prev,
          selectedGenerations: [...prev.selectedGenerations, genId]
        };
      }
    });
  };

  const handleGenderChange = (gender, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        genders: [...prev.genders, gender]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        genders: prev.genders.filter(g => g !== gender)
      }));
    }
  };

  // Calculate combined age range from selected generations
  const getAgeRange = () => {
    if (formData.selectedGenerations.length === 0) {
      return { min: 18, max: 65 };
    }
    
    const selectedGens = GENERATIONS.filter(g => 
      formData.selectedGenerations.includes(g.id)
    );
    
    const ageMin = Math.min(...selectedGens.map(g => g.ageMin)); // Remove Math.max(18, ...) to allow Gen Alpha!
    const ageMax = Math.max(...selectedGens.map(g => g.ageMax));
    
    return { min: ageMin, max: ageMax };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.selectedGenerations.length === 0) {
      toast.error(t("setup.errors.selectGeneration"));
      return;
    }
    
    if (formData.genders.length === 0) {
      toast.error(t("setup.errors.selectGender"));
      return;
    }
    
    if (!formData.country) {
      toast.error(t("setup.errors.selectCountry"));
      return;
    }
    
    if (!formData.industry) {
      toast.error(t("setup.errors.selectIndustry"));
      return;
    }

    // Auto-generate study name based on selections
    const genNames = formData.selectedGenerations.map(id => 
      GENERATIONS.find(g => g.id === id)?.name[language] || GENERATIONS.find(g => g.id === id)?.name.en
    ).join(', ');
    const autoName = `${genNames} - ${formData.country} - ${new Date().toLocaleDateString()}`;

    setIsSubmitting(true);

    const ageRange = getAgeRange();

    try {
      console.log("🚀 Creating group...");
      const response = await axios.post(`${API}/target-groups`, {
        name: autoName, // Auto-generated name
        age_min: ageRange.min,
        age_max: ageRange.max,
        genders: formData.genders,
        country: formData.country,
        industry: formData.industry,
        agent_count: formData.agentCount,
        language: language  // Pass current UI language
      });
      
      console.log(`✅ Group created: ${response.data.id}`);
      toast.success(t("setup.success"));
      
      console.log(`🔄 Navigating to dashboard...`);
      navigate(`/dashboard/${response.data.id}`, {
        state: { expectedCount: formData.agentCount }
      });
      console.log(`✅ Navigation called`);
    } catch (error) {
      console.error("Error creating target group:", error);
      toast.error(t("setup.errors.failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const ageRange = getAgeRange();

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Hero gradient */}
      <div className="absolute inset-0 hero-gradient pointer-events-none" />

      {/* Navigation */}
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-12 py-6">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-2"
        >
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-500 to-amber-500 flex items-center justify-center">
            <Brain className="w-5 h-5 text-black" />
          </div>
          <span className="font-heading font-semibold text-lg text-white">Behaviour Brain</span>
        </motion.div>
        
        <div className="flex items-center gap-4">
          <LanguageSwitcher />
          <Button
            variant="ghost"
            onClick={() => navigate("/")}
            className="text-zinc-400 hover:text-white"
            data-testid="back-to-home-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t("setup.back")}
          </Button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative z-10 px-6 md:px-12 py-8 md:py-12">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">
              {t("setup.title")}
            </h1>
            <p className="text-zinc-400">
              {t("setup.subtitle")}
            </p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onSubmit={handleSubmit}
            className="glass rounded-2xl p-6 md:p-8 space-y-8"
          >
            {/* Study Name - REMOVED, will auto-generate */}

            {/* Generation Selection */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-white font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-yellow-400" />
                  {t("setup.targetGenerations")}
                </Label>
                <span className="text-sm text-zinc-400">
                  {t("setup.ageRange")}: {ageRange.min} - {ageRange.max}
                </span>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {GENERATIONS.map((gen) => {
                  const isSelected = formData.selectedGenerations.includes(gen.id);
                  const genName = typeof gen.name === 'object' ? gen.name[language] || gen.name.en : gen.name;
                  const genAgeRange = typeof gen.ageRange === 'object' ? gen.ageRange[language] || gen.ageRange.en : gen.ageRange;
                  
                  return (
                    <motion.button
                      key={gen.id}
                      type="button"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleGenerationToggle(gen.id)}
                      data-testid={`gen-${gen.id}-btn`}
                      className={`
                        relative p-4 rounded-xl border transition-all duration-200 text-left
                        ${isSelected 
                          ? 'border-yellow-500/50 bg-yellow-500/10' 
                          : 'border-white/5 bg-zinc-900/30 hover:border-white/10'}
                      `}
                    >
                      {/* Selection indicator */}
                      <div className={`
                        absolute top-3 right-3 w-5 h-5 rounded-full border-2 flex items-center justify-center
                        transition-colors duration-200
                        ${isSelected 
                          ? 'border-yellow-500 bg-yellow-500' 
                          : 'border-zinc-600'}
                      `}>
                        {isSelected && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      
                      {/* Gen name with gradient */}
                      <div className={`
                        text-sm font-semibold mb-1
                        ${isSelected ? 'text-white' : 'text-zinc-300'}
                      `}>
                        {genName}
                      </div>
                      
                      {/* Birth years */}
                      <div className="text-xs text-zinc-500 mb-1">
                        {language === 'de' ? 'Geboren' : 'Born'} {gen.birthYears}
                      </div>
                      
                      {/* Current age */}
                      <div className={`
                        inline-block px-2 py-0.5 rounded text-xs font-medium
                        ${isSelected 
                          ? 'bg-yellow-500/20 text-yellow-300' 
                          : 'bg-zinc-800 text-zinc-400'}
                      `}>
                        {genAgeRange}
                      </div>
                    </motion.button>
                  );
                })}
              </div>
              
              {formData.selectedGenerations.length === 0 && (
                <p className="text-sm text-red-400">{t("setup.selectGeneration")}</p>
              )}
            </div>

            {/* Gender Selection */}
            <div className="space-y-3">
              <Label className="text-white font-medium">{t("setup.gender")}</Label>
              <div className="flex flex-wrap gap-4">
                {[
                  { value: "male", label: t("setup.male") },
                  { value: "female", label: t("setup.female") },
                  { value: "other", label: t("setup.other") }
                ].map((gender) => (
                  <div key={gender.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`gender-${gender.value}`}
                      data-testid={`gender-${gender.value}-checkbox`}
                      checked={formData.genders.includes(gender.value)}
                      onCheckedChange={(checked) => handleGenderChange(gender.value, checked)}
                      className="border-zinc-600 data-[state=checked]:bg-yellow-500 data-[state=checked]:border-yellow-500"
                    />
                    <label
                      htmlFor={`gender-${gender.value}`}
                      className="text-sm text-zinc-300 cursor-pointer"
                    >
                      {gender.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Country */}
            <div className="space-y-3">
              <Label className="text-white font-medium">{t("setup.country")}</Label>
              <Select 
                value={formData.country} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
              >
                <SelectTrigger 
                  data-testid="country-select"
                  className="bg-zinc-900/50 border-zinc-800 h-12 text-white"
                >
                  <SelectValue placeholder={t("setup.selectCountry")} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {COUNTRIES.map((country) => (
                    <SelectItem 
                      key={country.value} 
                      value={country.value}
                      className="text-zinc-300 focus:bg-zinc-800 focus:text-white"
                    >
                      {country[language] || country.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Industry */}
            <div className="space-y-3">
              <Label className="text-white font-medium">{t("setup.industry")}</Label>
              <Select 
                value={formData.industry} 
                onValueChange={(value) => setFormData(prev => ({ ...prev, industry: value }))}
              >
                <SelectTrigger 
                  data-testid="industry-select"
                  className="bg-zinc-900/50 border-zinc-800 h-12 text-white"
                >
                  <SelectValue placeholder={t("setup.selectIndustry")} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {INDUSTRIES.map((industry) => (
                    <SelectItem 
                      key={industry.value} 
                      value={industry.value}
                      className="text-zinc-300 focus:bg-zinc-800 focus:text-white"
                    >
                      {industry[language] || industry.en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Number of Agents */}
            <div className="space-y-4">
              <Label className="text-white font-medium flex items-center gap-2">
                <Users className="w-4 h-4" />
                {t("setup.numberOfPersonas")}: {formData.agentCount}
              </Label>
              <Slider
                data-testid="agent-count-slider"
                value={[formData.agentCount]}
                onValueChange={([value]) => setFormData(prev => ({ ...prev, agentCount: value }))}
                min={1}
                max={50}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-zinc-500">
                <span>1</span>
                <span>25</span>
                <span>50</span>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              data-testid="generate-personas-btn"
              disabled={isSubmitting || formData.selectedGenerations.length === 0}
              className="w-full bg-yellow-500 text-black hover:bg-yellow-400 rounded-full h-12 font-medium group"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("setup.creating")}
                </>
              ) : (
                <>
                  {t("setup.generatePersonas")}
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
          </motion.form>
        </div>
      </main>
    </div>
  );
}
